import { updateCartCountDisplay } from "../../utils/cart"

class VariationProduct {
    constructor(el) {
        this.el = el
        this.form = el.querySelector("[data-variation-product-form]")
        this.priceDisplay = el.querySelector("[data-variation-product-price-display]")
        this.priceContent = this.priceDisplay ? this.priceDisplay.querySelector("[data-product-price-content]") : null
        this.addToCartButton = el.querySelector("[data-product-add-to-cart][data-product-type='variable']")
        this.viewCartLink = el.querySelector("[data-view-cart]")
        this.buttonLabel = this.addToCartButton ? this.addToCartButton.querySelector("[data-button-label]") : null
        this.variationIdInput = this.form ? this.form.querySelector("input[name='variation_id']") : null
        this.quantityInput = el.querySelector("[data-qty-input]")
        this.productId = this.form ? this.form.dataset.productId : null
        this.ajaxUrl = el.dataset.ajaxUrl || (typeof window !== "undefined" ? window.ajaxurl : null)
        this.variations = this.productId && window.wcVariationData ? window.wcVariationData[this.productId] : []
        this.attributeSelects = this.form ? Array.from(this.form.querySelectorAll("select[data-attribute-name]")) : []
        this.selectedVariation = null
        this.currentAttributes = {}
        this.initialPriceContentHTML = this.priceContent ? this.priceContent.innerHTML : ''
        this.originalButtonLabel = this.buttonLabel ? this.buttonLabel.textContent.trim() : ''
        this.buttonStateBeforeLoading = null

        this.handleAddToCart = this.handleAddToCart.bind(this)

        this.init()
    }

    init() {
        if (!this.form || !this.priceContent) {
            return
        }

        this.attributeSelects.forEach((select) => {
            select.addEventListener("change", () => this.handleAttributeChange())
        })

        if (this.addToCartButton) {
            this.addToCartButton.addEventListener("click", this.handleAddToCart)
        }

        // Handle initial state - this will check for pre-selected attributes
        // and set the button state accordingly
        this.handleAttributeChange()
    }

    handleAttributeChange() {
        if (!this.form) {
            return
        }

        const selectedAttributes = {}

        this.attributeSelects.forEach((select) => {
            const attributeName = select.dataset.attributeName
            const value = select.value

            if (attributeName && value) {
                selectedAttributes[`attribute_${attributeName}`] = value
            }
        })

        this.currentAttributes = selectedAttributes

        const allAttributesSelected = this.attributeSelects.length === Object.keys(selectedAttributes).length

        if (allAttributesSelected) {
            const matchingVariation = this.findMatchingVariation(selectedAttributes)

            if (matchingVariation) {
                this.selectedVariation = matchingVariation
                // Hide view cart link when variation changes
                this.hideViewCartLink()
                this.updateVariationDisplay(matchingVariation)
                return
            }
        }

        this.selectedVariation = null
        // Hide view cart link when variation is reset
        this.hideViewCartLink()
        this.resetToDefault()
        this.updateAddToCartButtonState(false)
    }

    findMatchingVariation(selectedAttributes) {
        return this.variations.find((variation) => {
            return Object.keys(selectedAttributes).every((key) => {
                const selectedValue = selectedAttributes[key]
                const variationValue = variation.attributes[key]

                if (!variationValue) {
                    return false
                }

                const normalizeValue = (val) => {
                    if (!val) return ""
                    return String(val).toLowerCase().trim()
                }

                const normalizedSelected = normalizeValue(selectedValue)
                const normalizedVariation = normalizeValue(variationValue)

                return (
                    normalizedVariation === normalizedSelected ||
                    normalizedVariation === normalizedSelected.replace(/\s+/g, "-") ||
                    normalizedSelected === normalizedVariation.replace(/\s+/g, "-")
                )
            })
        })
    }

    updateVariationDisplay(variation) {
        if (!variation) {
            return
        }

        if (this.variationIdInput) {
            this.variationIdInput.value = variation.variation_id
        }

        if (this.priceContent) {
            if (variation.is_in_stock && variation.is_purchasable) {
                this.priceContent.innerHTML = variation.price_html
            } else {
                this.priceContent.innerHTML = '<span class="text-lightgray">sold out</span>'
            }
        }

        if (this.addToCartButton) {
            if (variation.is_in_stock && variation.is_purchasable) {
                this.addToCartButton.dataset.variationId = variation.variation_id
                this.updateAddToCartButtonState(true)
            } else {
                this.addToCartButton.removeAttribute("data-variation-id")
                this.updateAddToCartButtonState(false)
            }
        }
    }

    resetToDefault() {
        if (this.variationIdInput) {
            this.variationIdInput.value = 0
        }

        if (this.priceContent && this.initialPriceContentHTML) {
            this.priceContent.innerHTML = this.initialPriceContentHTML
        }

        if (this.addToCartButton) {
            this.addToCartButton.removeAttribute("data-variation-id")
        }
    }

    updateAddToCartButtonState(isEnabled) {
        if (!this.addToCartButton) {
            return
        }

        if (isEnabled) {
            this.addToCartButton.hidden = false
            this.addToCartButton.removeAttribute("hidden")
            this.addToCartButton.disabled = false
            this.addToCartButton.setAttribute("aria-disabled", "false")
        } else {
            this.addToCartButton.hidden = true
            this.addToCartButton.setAttribute("hidden", "")
            this.addToCartButton.disabled = true
            this.addToCartButton.setAttribute("aria-disabled", "true")
            this.clearLoadingState()
        }
    }

    getQuantity() {
        if (!this.quantityInput) {
            return 1
        }

        const value = parseInt(this.quantityInput.value, 10)
        if (Number.isNaN(value) || value <= 0) {
            return 1
        }

        const min = this.quantityInput.getAttribute("min")
        const max = this.quantityInput.getAttribute("max")

        let quantity = value

        if (min !== null) {
            quantity = Math.max(quantity, parseInt(min, 10) || 1)
        }

        if (max !== null && !Number.isNaN(parseInt(max, 10))) {
            quantity = Math.min(quantity, parseInt(max, 10))
        }

        return quantity
    }

    setLoading(isLoading) {
        if (!this.addToCartButton) {
            return
        }

        if (isLoading) {
            this.buttonStateBeforeLoading = {
                disabled: this.addToCartButton.disabled,
                hidden: this.addToCartButton.hidden,
            }

            this.addToCartButton.classList.add("is-loading")
            this.addToCartButton.disabled = true
            this.addToCartButton.hidden = false
            this.addToCartButton.removeAttribute("hidden")
            this.addToCartButton.setAttribute("aria-busy", "true")
        } else {
            // Check if view cart link is visible - if so, keep button hidden
            const shouldKeepButtonHidden = this.viewCartLink && !this.viewCartLink.hidden

            if (this.buttonStateBeforeLoading) {
                this.addToCartButton.disabled = this.buttonStateBeforeLoading.disabled

                if (shouldKeepButtonHidden) {
                    // Keep button hidden if view cart link is visible
                    this.addToCartButton.hidden = true
                    this.addToCartButton.setAttribute("hidden", "")
                } else if (this.buttonStateBeforeLoading.hidden) {
                    this.addToCartButton.hidden = true
                    this.addToCartButton.setAttribute("hidden", "")
                } else {
                    this.addToCartButton.hidden = false
                    this.addToCartButton.removeAttribute("hidden")
                }
            } else {
                this.addToCartButton.disabled = false
                if (shouldKeepButtonHidden) {
                    this.addToCartButton.hidden = true
                    this.addToCartButton.setAttribute("hidden", "")
                }
            }

            this.addToCartButton.classList.remove("is-loading")
            this.addToCartButton.setAttribute("aria-busy", "false")
            this.buttonStateBeforeLoading = null
        }
    }

    clearLoadingState() {
        if (!this.addToCartButton) {
            return
        }

        this.addToCartButton.classList.remove("is-loading")
        this.addToCartButton.removeAttribute("aria-busy")
        this.buttonStateBeforeLoading = null
    }

    showViewCartLink() {
        if (!this.viewCartLink) {
            return
        }

        this.viewCartLink.hidden = false
        this.viewCartLink.removeAttribute("hidden")
        
        // Hide the "Add to cart" button
        if (this.addToCartButton) {
            this.addToCartButton.hidden = true
            this.addToCartButton.setAttribute("hidden", "")
        }
    }

    hideViewCartLink() {
        if (!this.viewCartLink) {
            return
        }

        this.viewCartLink.hidden = true
        this.viewCartLink.setAttribute("hidden", "")
    }

    flashSuccessState() {
        if (!this.buttonLabel || !this.originalButtonLabel) {
            return
        }

        this.buttonLabel.textContent = "ajouté !"

        window.setTimeout(() => {
            this.buttonLabel.textContent = this.originalButtonLabel
        }, 2000)
    }

    async handleAddToCart(event) {
        event.preventDefault()

        if (!this.ajaxUrl || !this.productId) {
            console.error("Add to cart configuration is incomplete")
            return
        }

        if (!this.selectedVariation || !this.selectedVariation.variation_id) {
            window.alert("Veuillez sélectionner une variation disponible avant d'ajouter au panier.")
            return
        }

        const quantity = this.getQuantity()

        const payload = new URLSearchParams()
        payload.append("action", "lc_add_to_cart")
        payload.append("product_id", this.productId)
        payload.append("add-to-cart", this.productId)
        payload.append("quantity", quantity)
        payload.append("variation_id", this.selectedVariation.variation_id)

        Object.entries(this.currentAttributes).forEach(([key, value]) => {
            payload.append(`variation[${key}]`, value)
        })

        this.setLoading(true)

        try {
            const response = await fetch(this.ajaxUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
                body: payload.toString(),
            })

            const result = await response.json()

            if (!response.ok || !result || !result.success) {
                const errorMessage = result?.data?.message || "Impossible d'ajouter le produit au panier."
                throw new Error(errorMessage)
            }

            this.showViewCartLink()
            this.flashSuccessState()

            if (result?.data?.cart_count !== undefined) {
                updateCartCountDisplay(result.data.cart_count)

                window.dispatchEvent(
                    new CustomEvent("cart:updated", {
                        detail: {
                            count: result.data.cart_count,
                        },
                    })
                )
            }
        } catch (error) {
            console.error(error)
            window.alert(error.message || "Une erreur est survenue lors de l'ajout au panier.")
        } finally {
            this.setLoading(false)
        }
    }

    // Method to get current variation data (for potential future usage)
    getCurrentVariation() {
        return this.selectedVariation
    }

    // Method to get current variation ID (for potential future usage)
    getCurrentVariationId() {
        return this.selectedVariation ? this.selectedVariation.variation_id : 0
    }
}

export default VariationProduct